import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { IoWarningOutline } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import ClientAuthContext from "../../context/ClientAuthContext";
import { serverInstance } from "../../service/axios";
import "./mock-test.scss";
import { getExamEntryWindowUnixMs } from "./mockTestSchedule";

const LETTERS = ["A", "B", "C", "D", "E", "F", "G"];

const getAnswerChoices = (question, labels) => {
  const multiChoiceMap = labels?.multiChoiceAnswers || {};
  const multiChoiceAnswers = [
    { key: "A", label: multiChoiceMap.A || "1, 2, 3 зөв" },
    { key: "B", label: multiChoiceMap.B || "1, 2, 3, 4 зөв" },
    { key: "C", label: multiChoiceMap.C || "2, 4 зөв" },
    { key: "D", label: multiChoiceMap.D || "4 зөв" },
    { key: "E", label: multiChoiceMap.E || "Бүгд зөв" }
  ];

  if (question.answer_mode === "MULTI_CHOICE") {
    return multiChoiceAnswers;
  }
  return (question.options || []).map((option, index) => ({
    key: LETTERS[index],
    label: option
  }));
};

const getTimeSpentDisplay = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

const formatClock = (totalSeconds) => {
  const safe = Math.max(0, Number(totalSeconds) || 0);
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const htmlToText = (html = "") => html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").trim();

const LEADING_QUESTION_NUMBER_PATTERN =
  /^((?:\s|&nbsp;)*(?:<[^>]+>(?:\s|&nbsp;)*)*(?:(?:<strong>|<b>)(?:\s|&nbsp;)*)?)(\d{1,4})\s*[.)]\s*(?:(?:<\/strong>|<\/b>)(?:\s|&nbsp;)*)?/i;

const normalizeQuestionTextNumber = (value = "", sourceNumber, displayNumber) => {
  const raw = String(value || "");
  const source = Number(sourceNumber);
  const display = Number(displayNumber);

  if (!Number.isFinite(source) || !Number.isFinite(display) || source === display) {
    return raw;
  }

  return raw.replace(LEADING_QUESTION_NUMBER_PATTERN, (match, prefix, leadingNumber) => {
    return Number(leadingNumber) === source ? `${prefix}${display}. ` : match;
  });
};

const parseStoredProgress = (rawValue) => {
  if (!rawValue) {
    return null;
  }
  try {
    const parsed = JSON.parse(rawValue);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_error) {
    return null;
  }
};

const RichText = ({ value, className = "" }) => (
  <div className={className} dangerouslySetInnerHTML={{ __html: value || "" }} />
);

const resolveGraphicUrl = (graphicPath = "") => {
  const raw = String(graphicPath || "").trim();
  if (!raw) {
    return "";
  }
  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    return raw;
  }
  return `https://msid-asset.s3.ap-northeast-2.amazonaws.com/${raw}`;
};

const normalizePassage = (passageCandidate = {}) => {
  const title = passageCandidate.title || passageCandidate.name || "";
  const text =
    passageCandidate.content ||
    passageCandidate.text ||
    passageCandidate.passage_text ||
    passageCandidate.description ||
    "";

  if (!title && !text) return null;
  return { title, text };
};

const getPassageForQuestion = ({ question, questionOrder, passages = [] }) => {
  const directPassageCandidate =
    question?.passage_item || question?.passageItem || question?.passage || question?.linkedPassage || null;
  const directPassage = normalizePassage(directPassageCandidate || {});
  if (directPassage) {
    return directPassage;
  }

  if (!Array.isArray(passages) || passages.length === 0) {
    return null;
  }

  const linkedByRange = passages.find((passage) => {
    const start = Number(
      passage?.start_pos ?? passage?.start_index ?? passage?.startIndex ?? passage?.startPos ?? NaN
    );
    const end = Number(
      passage?.end_pos ?? passage?.end_index ?? passage?.endIndex ?? passage?.endPos ?? NaN
    );

    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      return false;
    }

    return questionOrder >= start && questionOrder <= end;
  });

  return normalizePassage(linkedByRange || {});
};

export default function MockTest({ testId, onBack, data }) {
  const navigate = useNavigate();
  const params = useParams();
  const { userData } = useContext(ClientAuthContext);
  const labels = data?.MockTest || {};
  const resolvedTestId = testId || params.id;
  const isLoggedIn = Boolean(userData?.email);
  const isMembershipActive = userData?.paid_until && new Date(userData.paid_until) > new Date();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [finishedByTimeout, setFinishedByTimeout] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const didHydrateProgressRef = useRef(false);
  const progressStorageKey = useMemo(() => {
    if (!resolvedTestId) {
      return "";
    }
    return `msid-mock-test-progress-${resolvedTestId}`;
  }, [resolvedTestId]);
  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    navigate("/tests");
  };
  const showBackButton = Boolean(onBack) || Boolean(params.id);

  useEffect(() => {
    if (!resolvedTestId) {
      setLoading(false);
      return;
    }
    if (!isLoggedIn || !isMembershipActive) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("msidclienttoken");
    setLoading(true);
    setError("");
    setTest(null);
    serverInstance
      .get(`/client/tests/${resolvedTestId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      })
      .then((res) => {
        setTest(res.data?.item || null);
      })
      .catch((err) => {
        const raw =
          err?.response?.data?.message ||
          (typeof err?.response?.data === "string" ? err.response.data : null) ||
          err?.response?.data;
        const message = raw || "Mock test дуудах үед алдаа гарлаа.";
        setError(
          typeof message === "string"
            ? message
            : labels.fetchTestError || "Mock test дуудах үед алдаа гарлаа."
        );
      })
      .finally(() => setLoading(false));
  }, [isLoggedIn, isMembershipActive, labels.fetchTestError, resolvedTestId]);

  useEffect(() => {
    if (!started || finished) {
      return;
    }
    const timer = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [started, finished]);

  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const timeLimitSeconds = useMemo(() => {
    const minutes = Number(test?.test_minutes);
    if (!Number.isFinite(minutes) || minutes <= 0) {
      return 0;
    }
    return Math.floor(minutes) * 60;
  }, [test?.test_minutes]);

  useEffect(() => {
    if (!started || finished || timeLimitSeconds <= 0) {
      return;
    }
    if (elapsedSeconds >= timeLimitSeconds) {
      setFinishedByTimeout(true);
      setFinished(true);
    }
  }, [elapsedSeconds, finished, started, timeLimitSeconds]);

  const questions = useMemo(() => {
    if (!test?.questions) {
      return [];
    }
    return [...test.questions].sort((a, b) => Number(a.index) - Number(b.index));
  }, [test]);

  const totalQuestions = questions.length;
  const answeredCount = Object.values(answers).filter(Boolean).length;
  const unansweredCount = Math.max(totalQuestions - answeredCount, 0);
  const currentQuestion = questions[currentIndex];
  const currentQuestionOrder = Number(currentQuestion?.index) || currentIndex + 1;
  const currentPassage = useMemo(
    () =>
      getPassageForQuestion({
        question: currentQuestion,
        questionOrder: currentQuestionOrder,
        passages: test?.passages || []
      }),
    [currentQuestion, currentQuestionOrder, test?.passages]
  );

  const result = useMemo(() => {
    if (!finished || totalQuestions === 0) {
      return null;
    }
    let correctCount = 0;
    const wrongAnswers = [];
    questions.forEach((question, index) => {
      const selected = answers[index] || "";
      const correct = question.correct_answer;
      if (selected === correct) {
        correctCount += 1;
      } else {
        wrongAnswers.push({
          question,
          index,
          selected
        });
      }
    });
    const percentage = Math.round((correctCount / totalQuestions) * 100);
    return { correctCount, wrongAnswers, percentage };
  }, [answers, finished, questions, totalQuestions]);

  useEffect(() => {
    didHydrateProgressRef.current = false;
  }, [progressStorageKey]);

  useEffect(() => {
    if (!progressStorageKey || !test || didHydrateProgressRef.current) {
      return;
    }
    const storedProgress = parseStoredProgress(localStorage.getItem(progressStorageKey));
    didHydrateProgressRef.current = true;
    if (!storedProgress) {
      return;
    }

    const savedAnswers =
      storedProgress.answers && typeof storedProgress.answers === "object" ? storedProgress.answers : {};
    const filteredAnswers = Object.entries(savedAnswers).reduce((acc, [key, value]) => {
      const idx = Number(key);
      if (Number.isInteger(idx) && idx >= 0 && idx < totalQuestions && typeof value === "string") {
        acc[idx] = value;
      }
      return acc;
    }, {});

    const restoredStarted = Boolean(storedProgress.started) || Boolean(storedProgress.finished);
    const restoredFinished = Boolean(storedProgress.finished);
    const restoredCurrentIndex = Number(storedProgress.currentIndex);

    setStarted(restoredStarted);
    setFinished(restoredFinished);
    setFinishedByTimeout(Boolean(storedProgress.finishedByTimeout));
    setAnswers(filteredAnswers);
    setElapsedSeconds(Math.max(0, Number(storedProgress.elapsedSeconds) || 0));
    setCurrentIndex(
      Number.isInteger(restoredCurrentIndex)
        ? Math.min(Math.max(restoredCurrentIndex, 0), Math.max(totalQuestions - 1, 0))
        : 0
    );
  }, [progressStorageKey, test, totalQuestions]);

  useEffect(() => {
    if (!progressStorageKey || !test || !didHydrateProgressRef.current) {
      return;
    }
    if (!started && !finished) {
      return;
    }

    localStorage.setItem(
      progressStorageKey,
      JSON.stringify({
        started,
        finished,
        currentIndex,
        answers,
        elapsedSeconds,
        finishedByTimeout
      })
    );
  }, [
    answers,
    currentIndex,
    elapsedSeconds,
    finished,
    finishedByTimeout,
    progressStorageKey,
    started,
    test
  ]);

  const handleSelectAnswer = (questionIndex, answerValue) => {
    setAnswers((prev) => ({
      ...prev,
      [questionIndex]: answerValue
    }));
  };

  const handleStart = () => {
    if (progressStorageKey) {
      localStorage.removeItem(progressStorageKey);
    }
    setStarted(true);
    setFinished(false);
    setFinishedByTimeout(false);
    setCurrentIndex(0);
    setElapsedSeconds(0);
    setAnswers({});
  };

  const handleFinish = () => {
    const shouldFinish = window.confirm(
      labels.finishConfirmation || "Шалгалтаа дуусгахдаа итгэлтэй байна уу?"
    );
    if (!shouldFinish) {
      return;
    }
    setFinishedByTimeout(false);
    setFinished(true);
  };

  if (loading) {
    return (
      <div className="container py-4" id="mock-test">
        <div className="d-flex justify-content-center align-items-center gap-2 text-secondary">
          <AiOutlineLoading3Quarters className="loading-spinner" size={24} />
          <span>{labels.loadingTest || "Сорил шалгалт ачаалж байна..."}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-4" id="mock-test">
        <div className="alert alert-danger">{error}</div>
        {showBackButton ? (
          <button className="btn btn-outline-secondary" onClick={handleBack}>
            {labels.backToList || "Тестүүд рүү буцах"}
          </button>
        ) : null}
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="container py-4" id="mock-test">
        <div className="alert alert-warning d-flex align-items-center gap-2 mb-3">
          <IoWarningOutline size={18} />
          <span>{labels.loginRequired || "Сорил нээхийн тулд нэвтэрнэ үү."}</span>
        </div>
        {showBackButton ? (
          <button className="btn btn-outline-secondary" onClick={handleBack}>
            {labels.backToList || "Тестүүд рүү буцах"}
          </button>
        ) : null}
      </div>
    );
  }

  if (!isMembershipActive) {
    return (
      <div className="container py-4" id="mock-test">
        <div className="alert alert-warning d-flex align-items-center gap-2 mb-3">
          <IoWarningOutline size={18} />
          <span>{labels.payment || "Энэхүү сорилыг үзэхийн тулд гишүүнчлэл идэвхтэй байх шаардлагатай."}</span>
        </div>
        {showBackButton ? (
          <button className="btn btn-outline-secondary" onClick={handleBack}>
            {labels.backToList || "Тестүүд рүү буцах"}
          </button>
        ) : null}
      </div>
    );
  }

  if (!test) {
    return null;
  }

  const examLockedReason = (() => {
    if (test.type !== "EXAM") {
      return null;
    }
    const w = getExamEntryWindowUnixMs(test.test_dt, test.test_minutes);
    if (!w) {
      return labels.examScheduleInvalid || "Шалгалтын хуваарь тохируулаагүй байна.";
    }
    if (nowMs < w.openMs) {
      return labels.examNotYetOpen || "Шалгалт зөвхөн эхлэх цагтаа нээгдэнэ (УБ цаг).";
    }
    if (nowMs >= w.closeMs) {
      return labels.examPeriodEnded || "Шалгалтыг нээх хугацаа дууссан (дараагийн өдрийн 00:00 УБ).";
    }
    return null;
  })();

  if (examLockedReason) {
    return (
      <div className="container py-4" id="mock-test">
        <div className="alert alert-warning d-flex align-items-center gap-2 mb-3">
          <IoWarningOutline size={18} />
          <span>{examLockedReason}</span>
        </div>
        {showBackButton ? (
          <button className="btn btn-outline-secondary" onClick={handleBack}>
            {labels.backToList || "Тестүүд рүү буцах"}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="container py-4" id="mock-test">
      <div className="card shadow-sm border-0">
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
            <div>
              <h3 className="mb-1">{test.name}</h3>
              <p className="text-muted mb-0">{test.description || labels.noDescription || "Тайлбар байхгүй."}</p>
            </div>
            {showBackButton ? (
              <button className="btn btn-outline-secondary btn-sm" onClick={handleBack}>
                {labels.backToList || "Тестүүд рүү буцах"}
              </button>
            ) : null}
          </div>

          {!started && !finished ? (
            <div className="bg-light rounded-3 p-4">
              <h5 className="mb-3">{labels.overviewTitle || "Шалгалтын тойм"}</h5>
              <div className="row g-3 mb-4">
                <div className="col-md-4">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small">{labels.totalQuestions || "Нийт асуулт"}</div>
                    <div className="fs-4 fw-bold">{totalQuestions}</div>
                  </div>
                </div>
                {timeLimitSeconds > 0 ? (
                  <div className="col-md-4">
                    <div className="border rounded p-3 h-100">
                      <div className="text-muted small">{labels.timeLimit || "Хугацаа"}</div>
                      <div className="fs-4 fw-bold">
                        {Math.floor(timeLimitSeconds / 60)}
                        {labels.timeLimitMinutesSuffix || " мин"}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <button className="btn btn-primary btn-lg" onClick={handleStart} disabled={totalQuestions === 0}>
                {test.type === "EXAM"
                  ? labels.startExamButton || "Шалгалт эхлэх"
                  : labels.startButton || "Сорил шалгалт эхлэх"}
              </button>
            </div>
          ) : null}

          {started && !finished && currentQuestion ? (
            <>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 bg-light p-3 rounded mb-3">
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge text-bg-secondary">
                    {labels.total || "Нийт"}: {totalQuestions}
                  </span>
                  <span className="badge text-bg-primary">
                    {labels.current || "Одоогийн"}: {currentIndex + 1}
                  </span>
                  <span className="badge text-bg-primary">
                    {labels.answered || "Хариулсан"}: {answeredCount}
                  </span>
                </div>
                <span className={`fw-semibold ${timeLimitSeconds > 0 ? "text-danger" : "text-secondary"}`}>
                  {timeLimitSeconds > 0
                    ? `${labels.timeRemaining || "Үлдсэн хугацаа"}: ${formatClock(
                        Math.max(0, timeLimitSeconds - elapsedSeconds)
                      )}`
                    : `${labels.elapsed || "Зарцуулсан"}: ${getTimeSpentDisplay(elapsedSeconds)}`}
                </span>
              </div>

              <div className="d-flex flex-wrap gap-2 mb-3">
                {questions.map((_, index) => (
                  <button
                    key={`q-nav-${index}`}
                    type="button"
                    className={`btn btn-sm ${
                      currentIndex === index
                        ? "btn-primary"
                        : answers[index]
                        ? "btn-outline-primary"
                        : "btn-outline-warning"
                    }`}
                    onClick={() => setCurrentIndex(index)}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>

              {unansweredCount > 0 ? (
                <div className="alert alert-warning py-2 px-3 mb-3">
                  {labels.unanswered || "Хариулаагүй"}: {unansweredCount}
                </div>
              ) : null}

              <div className="border rounded p-4">
                {/* <h5 className="mb-3">4. Current Question UI</h5> */}
                {currentPassage ? (
                  <div className="border rounded bg-light p-3 mb-3">
                    {currentPassage.title ? <h6 className="mb-2">{currentPassage.title}</h6> : null}
                    {currentPassage.text ? <RichText value={currentPassage.text} /> : null}
                  </div>
                ) : null}

                <div className="mb-3">
                  <RichText
                    value={normalizeQuestionTextNumber(
                      currentQuestion.question_text,
                      currentQuestion.index,
                      currentIndex + 1
                    )}
                  />
                </div>
                {currentQuestion?.graphic ? (
                  <div className="mb-3">
                    <img
                      src={resolveGraphicUrl(currentQuestion.graphic)}
                      alt="Question graphic"
                      style={{ maxWidth: "100%", maxHeight: 360, objectFit: "contain" }}
                    />
                  </div>
                ) : null}

                {currentQuestion.answer_mode === "MULTI_CHOICE" && (
                  <div className="mb-4">
                    {(currentQuestion.options || []).map((option, index) => (
                      <div key={`statement-${index}`} className="d-flex gap-2 mb-2">
                        <strong>{index + 1}.</strong>
                        <RichText value={option} />
                      </div>
                    ))}
                  </div>
                )}

                <div className="d-grid gap-2">
                  {getAnswerChoices(currentQuestion, labels).map((choice) => (
                    <label
                      key={`choice-${choice.key}`}
                      className={`border rounded p-3 d-flex gap-2 align-items-center ${
                        answers[currentIndex] === choice.key ? "border-primary bg-primary-subtle" : ""
                      }`}
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        type="radio"
                        name={`question-${currentIndex}`}
                        checked={answers[currentIndex] === choice.key}
                        onChange={() => handleSelectAnswer(currentIndex, choice.key)}
                      />
                      <span>
                        <strong>{choice.key}.</strong>{" "}
                        {currentQuestion.answer_mode === "SINGLE_CHOICE" ? (
                          <span dangerouslySetInnerHTML={{ __html: choice.label || "" }} />
                        ) : (
                          choice.label
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="d-flex justify-content-between mt-3">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
                  disabled={currentIndex === 0}
                >
                  {labels.previous || "Өмнөх"}
                </button>
                {currentIndex < totalQuestions - 1 ? (
                  <button
                    className="btn btn-primary"
                    onClick={() => setCurrentIndex((prev) => Math.min(totalQuestions - 1, prev + 1))}
                  >
                    {labels.next || "Дараах"}
                  </button>
                ) : (
                  <button
                    className={`btn ${unansweredCount > 0 ? "btn-warning" : "btn-danger"}`}
                    onClick={handleFinish}
                  >
                    {labels.submit || "Шалгалт дуусгах"}
                  </button>
                )}
              </div>
            </>
          ) : null}

          {finished && result ? (
            <div className="mt-2">
              <h5 className="mb-3">{labels.resultTitle || "Үр дүн"}</h5>
              {finishedByTimeout && timeLimitSeconds > 0 ? (
                <div className="alert alert-info py-2 px-3 mb-3">
                  {labels.timeUpMessage || "Хугацаа дууссан тул шалгалт автоматаар дууслаа."}
                </div>
              ) : null}
              <div className="row g-3 mb-4">
                <div className="col-md-4">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small">{labels.timeSpent || "Зарцуулсан хугацаа"}</div>
                    <div className="fs-5 fw-bold">{getTimeSpentDisplay(elapsedSeconds)}</div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small">{labels.score || "Оноо"}</div>
                    <div className="fs-5 fw-bold">
                      {result.correctCount}/{totalQuestions}
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted small">{labels.percentage || "Хувь"}</div>
                    <div className="fs-5 fw-bold">{result.percentage}%</div>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">
                  {labels.wrongAnswers || "Буруу хариултууд"} ({result.wrongAnswers.length})
                </h6>
                <button className="btn btn-outline-primary btn-sm" onClick={handleStart}>
                  {labels.retry || "Дахин өгөх"}
                </button>
              </div>

              {result.wrongAnswers.length === 0 ? (
                <div className="alert alert-success mb-0">
                  {labels.allCorrect || "Баяр хүргэе! Бүх асуултад зөв хариулсан байна."}
                </div>
              ) : (
                <div className="d-grid gap-3">
                  {result.wrongAnswers.map((wrongItem) => {
                    const questionChoices = getAnswerChoices(wrongItem.question, labels);
                    return (
                      <div className="border rounded p-3" key={`wrong-question-${wrongItem.index}`}>
                        <div className="fw-semibold mb-2">
                          {labels.questionLabel || "Асуулт"} {wrongItem.index + 1}
                        </div>
                        <RichText
                          value={normalizeQuestionTextNumber(
                            wrongItem.question.question_text,
                            wrongItem.question.index,
                            wrongItem.index + 1
                          )}
                          className="mb-3"
                        />
                        {wrongItem.question?.graphic ? (
                          <div className="mb-3">
                            <img
                              src={resolveGraphicUrl(wrongItem.question.graphic)}
                              alt="Question graphic"
                              style={{ maxWidth: "100%", maxHeight: 300, objectFit: "contain" }}
                            />
                          </div>
                        ) : null}

                        <div className="d-grid gap-2">
                          {questionChoices.map((choice) => {
                            const isCorrect = wrongItem.question.correct_answer === choice.key;
                            const isSelected = wrongItem.selected === choice.key;
                            return (
                              <div
                                key={`wrong-choice-${wrongItem.index}-${choice.key}`}
                                className={`rounded border p-2 ${
                                  isCorrect
                                    ? "border-success bg-success-subtle"
                                    : isSelected
                                    ? "border-danger bg-danger-subtle"
                                    : ""
                                }`}
                              >
                                <strong>{choice.key}.</strong>{" "}
                                {wrongItem.question.answer_mode === "SINGLE_CHOICE"
                                  ? htmlToText(choice.label)
                                  : choice.label}
                                {isCorrect ? (
                                  <span className="ms-2 badge text-bg-success">
                                    {labels.correct || "Зөв"}
                                  </span>
                                ) : null}
                                {isSelected ? (
                                  <span className="ms-2 badge text-bg-danger">
                                    {labels.selected || "Таны сонголт"}
                                  </span>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
